import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  Eye, 
  Calculator,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const AdRevenueProjections = () => {
  // Revenue projections based on industry benchmarks
  const projections = [
    {
      mau: 1000,
      pageViews: 15000,
      adImpressions: 12000,
      cpm: 2.5,
      rpm: 8.0,
      monthlyRevenue: 120,
      annualRevenue: 1440,
      status: 'beta'
    },
    {
      mau: 5000,
      pageViews: 75000,
      adImpressions: 60000,
      cpm: 3.0,
      rpm: 10.0,
      monthlyRevenue: 750,
      annualRevenue: 9000,
      status: 'growth'
    },
    {
      mau: 10000,
      pageViews: 150000,
      adImpressions: 120000,
      cpm: 3.5,
      rpm: 12.0,
      monthlyRevenue: 1800,
      annualRevenue: 21600,
      status: 'growth'
    },
    {
      mau: 50000,
      pageViews: 750000,
      adImpressions: 600000,
      cpm: 4.0,
      rpm: 15.0,
      monthlyRevenue: 11250,
      annualRevenue: 135000,
      status: 'scale'
    },
    {
      mau: 100000,
      pageViews: 1500000,
      adImpressions: 1200000,
      cpm: 5.0,
      rpm: 18.0,
      monthlyRevenue: 27000,
      annualRevenue: 324000,
      status: 'scale'
    }
  ];

  const assumptions = [
    { label: 'Avg Page Views per MAU', value: '15 pages/month' },
    { label: 'Ad Fill Rate', value: '80%' },
    { label: 'Ads per Page', value: '2-3 ads' },
    { label: 'Ad Network', value: 'Google AdSense' },
    { label: 'Audience Niche', value: 'Pet/Dog owners (premium CPM)' },
    { label: 'Guest vs Member Split', value: '70% guest (ads) / 30% members (no ads)' }
  ];

  const getStatusBadge = (status) => {
    const configs = {
      beta: { label: 'Beta', className: 'bg-blue-500' },
      growth: { label: 'Growth', className: 'bg-green-500' },
      scale: { label: 'Scale', className: 'bg-purple-500' }
    };
    const config = configs[status] || configs.beta;
    return <Badge className={`${config.className} text-white text-xs`}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="ad-revenue-projections">
      {/* Header */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-400" />
            Ad Revenue Projections
          </CardTitle>
          <CardDescription className="text-slate-400">
            Revenue estimates based on Monthly Active Users (MAU) and industry benchmarks for pet/lifestyle verticals.
            Ads are only shown to non-authenticated (guest) visitors.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Key Metrics */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="bg-emerald-900/30 border-emerald-700">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              <span className="text-xs text-emerald-300">Current Phase</span>
            </div>
            <p className="text-2xl font-bold text-white mt-2">Beta</p>
            <p className="text-xs text-emerald-400">~156 users</p>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-900/30 border-blue-700">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <Eye className="w-5 h-5 text-blue-400" />
              <span className="text-xs text-blue-300">Est. CPM</span>
            </div>
            <p className="text-2xl font-bold text-white mt-2">$2.50-$5.00</p>
            <p className="text-xs text-blue-400">Pet niche premium</p>
          </CardContent>
        </Card>
        
        <Card className="bg-purple-900/30 border-purple-700">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <TrendingUp className="w-5 h-5 text-purple-400" />
              <span className="text-xs text-purple-300">Target RPM</span>
            </div>
            <p className="text-2xl font-bold text-white mt-2">$8-$18</p>
            <p className="text-xs text-purple-400">Revenue per 1K views</p>
          </CardContent>
        </Card>
        
        <Card className="bg-amber-900/30 border-amber-700">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <Users className="w-5 h-5 text-amber-400" />
              <span className="text-xs text-amber-300">Ad Eligible</span>
            </div>
            <p className="text-2xl font-bold text-white mt-2">70%</p>
            <p className="text-xs text-amber-400">Guest visitors only</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Projections Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Calculator className="w-5 h-5 text-cyan-400" />
            Revenue by Scale
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700">
                <TableHead className="text-slate-400">MAU</TableHead>
                <TableHead className="text-slate-400">Page Views/mo</TableHead>
                <TableHead className="text-slate-400">Ad Impressions</TableHead>
                <TableHead className="text-slate-400">CPM</TableHead>
                <TableHead className="text-slate-400">Monthly Revenue</TableHead>
                <TableHead className="text-slate-400">Annual Revenue</TableHead>
                <TableHead className="text-slate-400">Phase</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projections.map((row, idx) => (
                <TableRow key={idx} className="border-slate-700">
                  <TableCell className="text-white font-medium">
                    {row.mau.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-slate-300">
                    {row.pageViews.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-slate-300">
                    {row.adImpressions.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-slate-300">
                    ${row.cpm.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-emerald-400 font-medium">
                    ${row.monthlyRevenue.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-emerald-400 font-medium">
                    ${row.annualRevenue.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(row.status)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Assumptions */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-400" />
            Assumptions & Notes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            {assumptions.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <span className="text-slate-300 text-sm">{item.label}</span>
                <span className="text-white font-medium text-sm">{item.value}</span>
              </div>
            ))}
          </div>
          
          <div className="mt-6 p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-white font-medium">Key Revenue Drivers</p>
                <ul className="text-slate-300 text-sm mt-2 space-y-1">
                  <li>- Pet owner audience commands premium CPM rates ($3-8 vs $1-2 general)</li>
                  <li>- Guest-only ads preserve member value proposition (ad-free experience)</li>
                  <li>- High engagement niche content increases page views per session</li>
                  <li>- SEO-friendly content attracts organic traffic (non-member visitors)</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <p className="text-white font-medium">Important Caveats</p>
                <ul className="text-slate-300 text-sm mt-2 space-y-1">
                  <li>- Actual revenue depends on ad fill rate and geographic distribution</li>
                  <li>- Q4 typically sees 20-40% higher CPMs (holiday advertising)</li>
                  <li>- Ad blockers may reduce effective impressions by 15-30%</li>
                  <li>- Premium placements (e.g., homepage hero) yield higher rates</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdRevenueProjections;
